import { MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { InputDisplay, SetMetricMutationData } from '@strength/domain-workout';
import { ReactNode, useEffect, useState } from 'react';

export const BaseNumericInput = ({
  display,
  workoutSetId,
  metricId,
  endAdornment,
  onEditSetMetric,
  units,
  disabled,
}: {
  display: InputDisplay;
  workoutSetId: string;
  metricId: string;
  endAdornment?: ReactNode;
  onEditSetMetric: (workoutSetId: string, value: SetMetricMutationData) => void;
  units: string[];
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState(String(display.value));
  const [unit, setUnit] = useState(display.unit ?? units?.[0] ?? '');
  // SC-515: these inputs are re-used when changing athletes, so the `useState` above
  // won't reset the initial value, so this forces the input to use the updated value
  useEffect(() => {
    setInputValue(String(display.value));
  }, [display.value]);

  const handleUnitChange = (e: SelectChangeEvent<string>) => {
    setUnit(e.target.value);
    onEditSetMetric(workoutSetId, {
      metricId,
      input: `${inputValue}${e.target.value}`,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const unitForValue = unit ? unit : units?.[0] ?? '';

    // Update the local state
    setUnit(unitForValue);
    setInputValue(value);

    // Don't update the value if it's a valid number that ends with a period
    // This will happen as a user is either typing a floating number or deleting from the end
    const isValidNumber = !isNaN(parseFloat(value));
    const isTypingDecimal = value.endsWith('.') || value.endsWith('.0');
    if (isValidNumber && !isTypingDecimal) {
      onEditSetMetric(workoutSetId, {
        metricId,
        input: `${value}${unitForValue}`,
      });
    }
  };

  const valueLength = String(
    display.value === '' ? display.placeholder : display.value,
  ).length;

  return (
    <div className="flex items-center gap-1">
      <TextField
        disabled={disabled}
        placeholder={String(display.placeholder)}
        value={inputValue}
        InputProps={{
          sx: {
            width: '100%',
            '& input': {
              minWidth: `${valueLength}ch`,
            },
          },
          endAdornment,
          inputMode: 'numeric',
        }}
        sx={{ mt: 0, width: '100%' }}
        onChange={handleChange}
      />
      {units.length > 0 && (
        <Select
          autoWidth
          id={`unit-select-${workoutSetId}-${metricId}`}
          disabled={units.length <= 1 || disabled}
          value={unit}
          onChange={handleUnitChange}
          endAdornment={null}
          sx={{
            width: '70px',
            minWidth: 'fit-content',
          }}
          IconComponent={() => null}
        >
          {units.map((unit) => (
            <MenuItem key={`${workoutSetId}-${unit}`} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </Select>
      )}
    </div>
  );
};
